import { Keypair } from "@solana/web3.js";
import { generateMnemonic, mnemonicToSeed, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";

export const EMAIL_WALLET_CRYPTO_VERSION = "email-wallet-v1";
export const EMAIL_WALLET_KEY_TYPE = "solana-bip39-ed25519-v1";

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
const HARDENED_OFFSET = 0x80000000;
const SOLANA_DERIVATION_PATH = [44, 501, 0, 0];
const DEFAULT_KDF_ITERATIONS = 250000;

function getBrowserCrypto() {
    const c = typeof window !== "undefined" ? window.crypto : null;
    if (!c?.subtle || typeof c.getRandomValues !== "function") {
        throw new Error("Secure browser crypto is not available.");
    }
    return c;
}

function concatBytes(...parts) {
    const len = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(len);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

function uint32Be(value) {
    const out = new Uint8Array(4);
    const v = value >>> 0;
    out[0] = (v >>> 24) & 0xff;
    out[1] = (v >>> 16) & 0xff;
    out[2] = (v >>> 8) & 0xff;
    out[3] = v & 0xff;
    return out;
}

function bytesToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.slice(i, i + chunkSize));
    }
    return btoa(binary);
}

function base64ToBytes(value) {
    const binary = atob(String(value || ""));
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
}

async function hmacSha512(keyBytes, dataBytes) {
    const subtle = getBrowserCrypto().subtle;
    const key = await subtle.importKey(
        "raw",
        keyBytes,
        { name: "HMAC", hash: "SHA-512" },
        false,
        ["sign"]
    );
    const sig = await subtle.sign("HMAC", key, dataBytes);
    return new Uint8Array(sig);
}

async function deriveSolanaSeedFromMnemonic(mnemonic) {
    const seed = await mnemonicToSeed(mnemonic, "");
    let i = await hmacSha512(TEXT_ENCODER.encode("ed25519 seed"), seed);
    let privateKey = i.slice(0, 32);
    let chainCode = i.slice(32);

    for (const index of SOLANA_DERIVATION_PATH) {
        const childIndex = (index + HARDENED_OFFSET) >>> 0;
        const data = concatBytes(new Uint8Array([0]), privateKey, uint32Be(childIndex));
        i = await hmacSha512(chainCode, data);
        privateKey = i.slice(0, 32);
        chainCode = i.slice(32);
    }

    return privateKey;
}

async function deriveAesKey(password, salt, iterations = DEFAULT_KDF_ITERATIONS) {
    const subtle = getBrowserCrypto().subtle;
    const baseKey = await subtle.importKey(
        "raw",
        TEXT_ENCODER.encode(String(password || "")),
        "PBKDF2",
        false,
        ["deriveKey"]
    );
    return subtle.deriveKey(
        {
            name: "PBKDF2",
            salt,
            iterations,
            hash: "SHA-256",
        },
        baseKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
    );
}

function normalizeMnemonic(mnemonic) {
    return String(mnemonic || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

export async function keypairFromRecoveryPhrase(mnemonic) {
    const normalized = normalizeMnemonic(mnemonic);
    if (!validateMnemonic(normalized, wordlist)) {
        throw new Error("Recovery phrase is not valid.");
    }
    const seed = await deriveSolanaSeedFromMnemonic(normalized);
    return Keypair.fromSeed(seed);
}

export async function encryptRecoveryPhrase(mnemonic, password) {
    if (!String(password || "")) {
        throw new Error("Password is required to encrypt the wallet.");
    }

    const normalized = normalizeMnemonic(mnemonic);
    if (!validateMnemonic(normalized, wordlist)) {
        throw new Error("Recovery phrase is not valid.");
    }

    const crypto = getBrowserCrypto();
    const salt = new Uint8Array(16);
    const iv = new Uint8Array(12);
    crypto.getRandomValues(salt);
    crypto.getRandomValues(iv);

    const key = await deriveAesKey(password, salt);
    const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        TEXT_ENCODER.encode(normalized)
    );

    return {
        version: EMAIL_WALLET_CRYPTO_VERSION,
        keyType: EMAIL_WALLET_KEY_TYPE,
        algorithm: "AES-GCM",
        ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
        iv: bytesToBase64(iv),
        kdf: {
            name: "PBKDF2",
            hash: "SHA-256",
            iterations: DEFAULT_KDF_ITERATIONS,
            salt: bytesToBase64(salt),
        },
    };
}

export async function decryptRecoveryPhrase(encryptedWallet, password) {
    if (!encryptedWallet || encryptedWallet.version !== EMAIL_WALLET_CRYPTO_VERSION) {
        throw new Error("Unsupported encrypted wallet version.");
    }
    if (encryptedWallet.keyType !== EMAIL_WALLET_KEY_TYPE) {
        throw new Error("Unsupported encrypted wallet key type.");
    }

    const salt = base64ToBytes(encryptedWallet?.kdf?.salt);
    const iv = base64ToBytes(encryptedWallet.iv);
    const ciphertext = base64ToBytes(encryptedWallet.ciphertext);
    const iterations = Number(encryptedWallet?.kdf?.iterations || DEFAULT_KDF_ITERATIONS);
    const key = await deriveAesKey(password, salt, iterations);

    try {
        const plaintext = await getBrowserCrypto().subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            ciphertext
        );
        return normalizeMnemonic(TEXT_DECODER.decode(plaintext));
    } catch {
        throw new Error("Could not decrypt wallet. Check the email password.");
    }
}

export async function createEncryptedEmailWallet(password) {
    const mnemonic = generateMnemonic(wordlist, 128);
    const keypair = await keypairFromRecoveryPhrase(mnemonic);
    const encryptedWallet = await encryptRecoveryPhrase(mnemonic, password);
    return {
        mnemonic,
        keypair,
        encryptedWallet,
        publicKey: keypair.publicKey.toBase58(),
    };
}

export async function unlockEncryptedEmailWallet(encryptedWallet, password, expectedPublicKey = "") {
    const mnemonic = await decryptRecoveryPhrase(encryptedWallet, password);
    const keypair = await keypairFromRecoveryPhrase(mnemonic);
    const publicKey = keypair.publicKey.toBase58();

    if (expectedPublicKey && publicKey !== expectedPublicKey) {
        throw new Error("Recovered wallet does not match the saved public key.");
    }

    return {
        mnemonic,
        keypair,
        publicKey,
    };
}

export async function importRecoveryPhraseAsEncryptedWallet(mnemonic, password) {
    const keypair = await keypairFromRecoveryPhrase(mnemonic);
    const encryptedWallet = await encryptRecoveryPhrase(mnemonic, password);
    return {
        keypair,
        encryptedWallet,
        publicKey: keypair.publicKey.toBase58(),
    };
}
